import React, {
  useCallback, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useFeatureFlagsContext, useI18n, useRootContext } from '@jarvis/react-portal-addons';
import NotificationsWidget from '../NotificationsWidget';
import PrinterCarousel from '../PrinterCarousel';
import useUsersApiCall from '../../../hooks/useUsersApiCall';
import useOpenStoreForHpApp from '../../../hooks/useOpenStoreForHpApp';
import { InstallTheHpAppButton, publishEvent } from '../../../utils/analytics';
import {
  Button,
  Container,
  MessageContainer,
  NotificationsWidgetContainer,
  PrinterCarouselContainer,
  Title,
  TitleAndNotificationsWrapper,
} from './styles';
import divHTMLRender from '../../../shared/divHTMLRender';

const Banner = ({
  advancedView, 'data-testid': dataTestId, deviceCache, showNotifications,
}) => {
  const [titleContainerHeight, setTitleContainerHeight] = useState(0);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);
  const [isTitleWidthOverHalf, setIsTitleWidthOverHalf] = useState(false);
  const { consolidatedPortalPrinterCarousel, consolidatedPortalGetAppButton } = useFeatureFlagsContext();
  const { stack, shell: { authProvider, store } } = useRootContext();
  const { t } = useI18n();
  const { openStoreForHpApp, hpAppUrl } = useOpenStoreForHpApp();
  const titleRef = useRef(null);
  const wrapperRef = useRef(null);
  const notificationsRef = useRef(null);

  const userInfosCall = useUsersApiCall({
    authProvider,
    stack,
    store,
  });

  const { data: userInfos } = userInfosCall;

  const {
    pending: isLoadingDevices,
    data: devices,
  } = deviceCache;

  const accountHasDevice = useMemo(() => devices && devices.length > 0, [devices]);
  const hasDevicesWithNotifications = useMemo(
    () => accountHasDevice && showNotifications,
    [accountHasDevice, showNotifications],
  );

  const showCarousel = useMemo(
    () => !isLoadingDevices && accountHasDevice,
    [accountHasDevice, isLoadingDevices],
  );

  const handleInstallTheHPAppButtonClick = useCallback(() => {
    publishEvent(InstallTheHpAppButton);
    openStoreForHpApp();
  }, [openStoreForHpApp]);

  useLayoutEffect(() => {
    function updateSize() {
      setTitleContainerHeight(titleRef.current?.offsetHeight);

      const titleWidth = titleRef.current?.offsetWidth + 47;
      const notificationsWidth = notificationsRef.current?.offsetWidth + 38;
      const wrapperWidth = wrapperRef.current?.offsetWidth;
      const isOverHalf = wrapperWidth >= 0
        && (titleWidth >= wrapperWidth * 0.5 || notificationsWidth >= wrapperWidth * 0.5);
      setIsTitleWidthOverHalf(isOverHalf);
      // isOverHalf is applied keeping the distance between title and notifications of 24px
    }
    window.addEventListener('resize', updateSize);
    // Delay initial measurement to ensure layout is complete and fonts are loaded
    const timeoutId = setTimeout(updateSize, 1000);
    return () => {
      window.removeEventListener('resize', updateSize);
      clearTimeout(timeoutId);
    };
  }, [userInfos?.data?.givenName, accountHasDevice]);

  return (
    <Container
      data-testid={dataTestId}
      className={[
        notificationsExpanded ? 'expanded' : '',
        isLoadingDevices || accountHasDevice ? 'has-devices' : '',
      ].join(' ')}
    >
      <TitleAndNotificationsWrapper
        ref={wrapperRef}
        titleHeight={titleContainerHeight}
        titleOverHalf={isTitleWidthOverHalf}
        className={[
          showNotifications ? '' : 'no-notifications',
          hasDevicesWithNotifications ? 'has-devices' : '',
          isTitleWidthOverHalf ? 'title-over-half' : '',
        ].join(' ')}
      >
        {showNotifications && (
          <NotificationsWidgetContainer
            ref={notificationsRef}
            data-testid="smart-notification-widget-mfe"
            titleHeight={titleContainerHeight}
            className={[
              hasDevicesWithNotifications ? 'has-devices' : '',
              notificationsExpanded ? 'expanded' : 'collapsed',
              isTitleWidthOverHalf ? 'title-over-half' : '',
            ].join(' ')}
          >
            <NotificationsWidget
              stack={stack} properties={{
                onCollapse: () => {
                  setNotificationsExpanded(false);
                },
                onExpand: () => {
                  setNotificationsExpanded(true);
                },
              }}
            />
          </NotificationsWidgetContainer>
        )}
        <Title
          className={[
            'title-regular',
            notificationsExpanded ? 'hide' : '',
            hasDevicesWithNotifications ? 'has-devices' : '',
          ].join(' ')}
          data-testid="user-name"
          ref={titleRef}
        >
          {t('dashboard.header.welcome.option1', {
            name: userInfos?.data?.givenName,
            interpolation: { escapeValue: false },
          })}
        </Title>
      </TitleAndNotificationsWrapper>

      {showCarousel && consolidatedPortalPrinterCarousel && (
        <PrinterCarouselContainer
          data-testid="printer-carousel-container"
          titleOverHalf={isTitleWidthOverHalf}
          className={[
            isTitleWidthOverHalf ? 'title-over-half' : '',
            notificationsExpanded ? 'expanded' : '',
          ].join(' ')}
        >
          <PrinterCarousel devices={devices} stack={stack} />
        </PrinterCarouselContainer>
      )}

      {!isLoadingDevices && !accountHasDevice && (
        advancedView ? (
          <MessageContainer>
            <p data-testid="manage-printer-title" className="subtitle-large">
              {t('dashboard.header.managePrinter.title')}
            </p>
            <p data-testid="manage-printer-message" className="body-regular">
              {divHTMLRender({
                text: t('dashboard.header.managePrinter.description'),
                className: 'body-regular',
                tagReplaceStart:
                  '<a href="https://support.hp.com/printer" target="_blank" rel="noopener noreferrer" '
                  + 'class="inline-support-link" data-testid="banner-support-hyperlink">',
                tagReplaceEnd: '</a>',
              })}
            </p>
          </MessageContainer>
        ) : (
          <MessageContainer>
            <p data-testid="connect-printer-title" className="subtitle-large">
              {t('dashboard.header.connectPrinter.title')}
            </p>
            <p data-testid="connect-printer-message" className="body-regular">
              {t('dashboard.header.connectPrinter.description')}
            </p>
            {consolidatedPortalGetAppButton && hpAppUrl
              && (
              <Button
                data-testid="connect-printer-button"
                appearance="primary"
                onClick={handleInstallTheHPAppButtonClick}
              >
                {t('dashboard.header.connectPrinter.button')}
              </Button>
              )}
          </MessageContainer>
        )
      )}
    </Container>
  );
};

Banner.defaultProps = {
  advancedView: false,
  'data-testid': null,
  showNotifications: true,
};

Banner.propTypes = {
  advancedView: PropTypes.bool,
  'data-testid': PropTypes.string,
  deviceCache: PropTypes.shape({
    pending: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.shape({
      message: PropTypes.string,
      code: PropTypes.number,
    }),
    isFetching: PropTypes.bool,
    forceFetch: PropTypes.func,
    makeApiCall: PropTypes.func,
    success: PropTypes.bool,
  }).isRequired,
  showNotifications: PropTypes.bool,
};

export default Banner;
